export const TRAINING_AI_DELAY_MS = 2000;

const TRAINING_AI_METHOD = 'training-mock';

export const delayTrainingAiResponse = () => new Promise(resolve => setTimeout(resolve, TRAINING_AI_DELAY_MS));

const jsonString = (payload) => JSON.stringify(payload);

const usageForPrompt = (prompt = '', response = '') => ({
    prompt_tokens: Math.ceil(String(prompt || '').length / 4),
    completion_tokens: Math.ceil(String(response || '').length / 4),
    total_tokens: Math.ceil((String(prompt || '').length + String(response || '').length) / 4),
    reasoningTokens: 0
});

const extractFirstJsonAfterLabel = (prompt = '', label = '') => {
    const source = String(prompt || '');
    const labelIndex = source.indexOf(label);
    if (labelIndex === -1) return null;

    const start = source.indexOf('{', labelIndex);
    if (start === -1) return null;

    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let index = start; index < source.length; index += 1) {
        const char = source[index];
        if (escaped) {
            escaped = false;
            continue;
        }
        if (char === '\\') {
            escaped = true;
            continue;
        }
        if (char === '"') {
            inString = !inString;
            continue;
        }
        if (inString) continue;
        if (char === '{') depth += 1;
        if (char === '}') {
            depth -= 1;
            if (depth === 0) {
                try {
                    return JSON.parse(source.slice(start, index + 1));
                } catch (error) {
                    return null;
                }
            }
        }
    }

    return null;
};

const detectLanguageCodes = (prompt = '') => {
    const codes = [];
    ['zh', 'en', 'ja', 'ko'].forEach((code) => {
        if (new RegExp(`"${code}"\\s*:`).test(prompt) || new RegExp(`languageTag"\\s*:\\s*"${code}"`).test(prompt)) {
            codes.push(code);
        }
    });
    return codes.length > 0 ? codes : ['zh', 'en'];
};

const pickTagFromIntroduction = (introduction = '') => {
    const text = String(introduction || '').toLowerCase();
    if (/coffee|cafe|咖啡|美食|餐|wine|bar/.test(text)) return '美食';
    if (/travel|japan|trip|旅行|旅遊|日本|探索/.test(text)) return '旅行';
    if (/art|museum|movie|music|展|電影|音樂|藝文/.test(text)) return '藝文';
    if (/sport|gym|run|hike|健身|運動|登山/.test(text)) return '運動';
    if (/language|english|mandarin|語言|英文|中文/.test(text)) return '語言';
    return '聊天';
};

const buildTagGenerationMock = (prompt) => {
    const profile = extractFirstJsonAfterLabel(prompt, 'participant_profile_json:') || {};
    const tagName = pickTagFromIntroduction(profile.introduction);
    return {
        resumeID: String(profile.resumeID || ''),
        selected_tags: [
            {
                name: tagName,
                reason: profile.introduction
                    ? `Training Mode mock: 根據自介內容，先以「${tagName}」作為練習標籤。`
                    : 'Training Mode mock: 自介資訊不足，僅提供保守練習標籤。',
                confidence: 0.82
            }
        ]
    };
};

const buildIntroRiskMock = () => ({
    analysis: {
        language_style: 'grounded',
        social_vibe: 'authentic'
    },
    interest_tags: [
        {
            display_tag: '咖啡',
            normalized_category: '美食/品飲',
            social_attribute: 'consumption'
        },
        {
            display_tag: '旅行',
            normalized_category: '旅遊/探索',
            social_attribute: 'social_buffer'
        }
    ],
    risk: {
        detected_flags: ['none'],
        reason: 'Training Mode mock: 自介內容具體且適合練習審核流程，未偵測到明顯風險。'
    },
    quality_score: 'A',
    recommended_action: 'allow'
});

const buildTagRiskMock = () => ({
    tag_risk: {
        severity: 'none',
        risk_score: 0,
        negative_tags: [],
        neutral_or_positive_tags: [
            { tagID: '101', name: 'Training VIP' },
            { tagID: '103', name: 'New Member' }
        ],
        reason: 'Training Mode mock: 既有標籤偏中性或正面，未形成負面風險訊號。'
    }
});

const buildUserRatingRiskMock = () => ({
    user_rating_risk: {
        severity: 'low',
        risk_score: 15,
        confidence: 'medium',
        summary: 'Training Mode mock: 歷史回饋大致穩定，僅保留低風險提醒。',
        positive_signal: {
            likes: 3,
            normals: 1,
            reason: '多數練習資料為正面或普通評價。'
        },
        negative_signal: {
            dislikes_count: 0,
            no_shows: 0,
            lates: 0,
            themes: []
        },
        recommended_action: 'allow',
        reason: 'Training Mode mock: 無具體負面評語、放鳥或遲到紀錄，建議通過。'
    }
});

const buildModel3Mock = () => ({
    matchesModel3: true,
    modelDescription: {
        zh: 'Training Mode mock：這桌因為美食、旅行與輕鬆聊天的共同興趣聚在一起，適合練習 Model 3 共同興趣分組描述。',
        en: 'Training Mode mock: This table is connected by shared interests in food, travel, and easy conversation, making it a safe Model 3 practice example.'
    },
    descriptions: {
        zh: 'Training Mode mock：這桌因為美食、旅行與輕鬆聊天的共同興趣聚在一起，適合練習 Model 3 共同興趣分組描述。',
        en: 'Training Mode mock: This table is connected by shared interests in food, travel, and easy conversation, making it a safe Model 3 practice example.'
    },
    matchDetails: [
        {
            label: '共同興趣：美食與旅行',
            field: 'tags/introduction',
            value: '美食/旅行',
            count: 3,
            participants: [
                { resumeID: '9001', value: '咖啡' },
                { resumeID: '9002', value: '旅行' },
                { resumeID: '9003', value: '美食' }
            ]
        }
    ],
    evaluationPoints: [
        'Training Mode mock: 至少 3 人有可延伸的美食或旅行話題。'
    ]
});

const buildDescriptionMock = (prompt) => {
    const languages = detectLanguageCodes(prompt);
    const modelDescription = {};
    languages.forEach((language) => {
        modelDescription[language] = language === 'en'
            ? 'Training Mode mock: This table has easy shared topics around food, travel, and first-time WKND practice conversations.'
            : language === 'ja'
                ? 'Training Mode mock: 食事と旅行の話題から会話を始めやすい練習用テーブルです。'
                : 'Training Mode mock：這桌以美食、旅行與新朋友練習交流作為共同話題，適合管理員熟悉分組描述流程。';
    });

    return {
        pickedModelNumber: 3,
        pickedModelName: '共同興趣桌',
        confidence: 0.86,
        reason: [
            'Training Mode mock: 多位參加者具備可延伸的共同興趣。',
            'Training Mode mock: 話題適合破冰且不涉及真實個資。'
        ],
        modelDescription,
        cautions: [
            'Training Mode mock: 實際上線時仍需檢查真實參加者資料。'
        ]
    };
};

const buildNotificationDetailsMock = (prompt) => {
    const languageTagMatch = String(prompt || '').match(/languageTag 為 "([^"]+)"/);
    const languageTag = languageTagMatch?.[1] || 'zh';
    const contents = [
        {
            languageTag: 'en',
            content: 'Training Mode mock: This is a safe practice notification detail. Use it to review layout, translations, and approval flow without sending a real campaign.'
        }
    ];
    if (languageTag !== 'en') {
        contents.push({
            languageTag,
            content: 'Training Mode mock：這是一段安全的練習用通知詳細內容，可用來熟悉編輯、翻譯與送出前檢查流程，不會觸發真實推播。'
        });
    }
    return { contents };
};

const buildFeedbackAnalysisMock = () => ({
    summary: 'Training Mode mock: 回饋整體穩定，適合用來練習閱讀 AI 分析區塊。',
    trendSummary: 'Training Mode mock: 推薦率與用戶正向回饋維持平穩，未顯示需要立即處理的異常波動。',
    turningPoints: [
        {
            weekOf: '2026-06-22',
            observation: '正向回饋略有提升。',
            possibleReason: '練習資料中的餐廳與分組體驗較一致。',
            evidence: ['recommendRate +3pp', 'userPositiveRate +2pp'],
            confidence: 'medium'
        }
    ],
    keyFindings: [
        {
            metric: 'recommendRate',
            status: 'stable',
            possibleReason: 'Training Mode mock 資料波動有限。',
            evidence: ['樣本為固定 mock dataset'],
            confidence: 'medium'
        }
    ],
    metricAnalysis: {
        recommendRate: '穩定偏正向。',
        notRecommendRate: '低且無明顯上升。',
        userPositiveRate: '穩定。',
        userNegativeRate: '低。',
        restaurantPositiveRate: '穩定。',
        restaurantNegativeRate: '低。'
    },
    rootCauseHypotheses: [
        {
            hypothesis: 'Training Mode mock 資料設計為低風險練習情境。',
            whyItFits: '多數指標保持穩定且正向。',
            counterEvidenceOrGap: '此資料不是正式營運資料。',
            nextCheck: '切回一般模式後再檢查真實區間資料。',
            confidence: 'high'
        }
    ],
    recommendedActions: [
        {
            priority: 'low',
            action: '用此結果練習複製、解讀與彙報流程。',
            reason: 'Training Mode 不會觸碰真實 API 或資料。',
            owner: 'CRM 管理員',
            successSignal: '管理員能完成分析流程並理解各區塊含義。'
        }
    ],
    followUpQuestions: ['正式分析前是否已切回一般模式？'],
    limitations: ['Training Mode mock 不代表真實會員或餐廳表現。']
});

const buildGenericMock = (prompt) => ({
    content: 'Training Mode mock：這是一段固定 AI 回覆，可用來練習操作流程，不會呼叫真實 AI API。',
    summary: 'Training Mode mock AI response.',
    promptLength: String(prompt || '').length
});

export const buildTrainingAiMockPayload = (prompt = '') => {
    const source = String(prompt || '');
    if (source.includes('participant_profile_json:') && source.includes('selected_tags')) {
        return buildTagGenerationMock(source);
    }
    if (source.includes('user_rating_risk')) {
        return buildUserRatingRiskMock();
    }
    if (source.includes('tag_risk')) {
        return buildTagRiskMock();
    }
    if (source.includes('user profile JSON:') && source.includes('quality_score') && source.includes('recommended_action')) {
        return buildIntroRiskMock();
    }
    if (source.includes('Model 3: Common Interests') || source.includes('matchesModel3')) {
        return buildModel3Mock();
    }
    if (source.includes('Stranger Dining Table Description Rewriter') || source.includes('selected_model:')) {
        return buildDescriptionMock(source);
    }
    if (source.includes('通知詳細廣宣內容') || source.includes('JSON 必須完全符合以下 schema')) {
        return buildNotificationDetailsMock(source);
    }
    if (source.includes('Feedback AI') || source.includes('feedback') || source.includes('recommendedActions')) {
        return buildFeedbackAnalysisMock();
    }
    if (source.includes('推播') || source.includes('廣宣內容')) {
        return {
            content: 'Training Mode mock：今晚留一點時間給新的連結與好運，來 WKND 練習一場安全、輕鬆、不會送出的推播。'
        };
    }
    return buildGenericMock(source);
};

export const buildTrainingAiResponse = (prompt = '') => {
    const payload = buildTrainingAiMockPayload(prompt);
    const response = jsonString(payload);
    return {
        response,
        usage: usageForPrompt(prompt, response),
        method: TRAINING_AI_METHOD
    };
};
